import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'
import { AuditLogsFilterParams } from '@/types/audit-log'
import { useAuditLogsStore } from '@/store'
import { exportAuditLogs } from '@/lib/api'

export const useAuditLogs = (params: AuditLogsFilterParams) => {
  const fetchAuditLogs = useAuditLogsStore(state => state.fetchAuditLogs)

  return useQuery({
    queryKey: ['audit-logs', params],
    queryFn: () => fetchAuditLogs(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useAuditLog = (id: string) => {
  const getAuditLog = useAuditLogsStore(state => state.getAuditLog)

  return useQuery({
    queryKey: ['audit-log', id],
    queryFn: () => getAuditLog(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useDeleteAuditLog = () => {
  const deleteAuditLog = useAuditLogsStore(state => state.deleteAuditLog)

  return useMutation({
    mutationFn: (id: string) => deleteAuditLog(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteAuditLogs = () => {
  const bulkDeleteAuditLogs = useAuditLogsStore(state => state.bulkDeleteAuditLogs)
  const deselectAllAuditLogs = useAuditLogsStore(state => state.deselectAllAuditLogs)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteAuditLogs(ids),
    onSuccess: response => {
      deselectAllAuditLogs()
      handleBulkDeleteSuccess(response)
    }
  })
}

export const useExportAuditLogs = () => {
  const t = useTranslations()

  return useMutation({
    mutationFn: (params: AuditLogsFilterParams) => exportAuditLogs(params),
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      if (filename) {
        a.download = filename || `audit-logs-${new Date().toISOString()}.xlsx`
      }
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_downloading_file'))
    }
  })
}
